import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../common/components/ui/dialog';
import { Button } from '../../../../common/components/ui/button';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import type { IOrderPositionDTOData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { acknowledgeBlockedPositionsDialog, changeDeliveryDateOfCart } from '../../actions/cartActions';
import { useCartPositionsWithBlockedState } from '../../state-hooks/useCartPositionsWithBlockedState';
import { formatDateByLanguage } from '~/common/utils/dateUtil';

export function CartDialogBlockedPositions() {
  const { t } = useTranslation();
  const { blockedPositionsOrderEndTimeExeeded, blockedPositionsNotAvailable, blockedPositionsOtherReasons } =
    useCartPositionsWithBlockedState();

  const canChangeDeliveryDate =
    blockedPositionsOrderEndTimeExeeded.length > 0 || blockedPositionsNotAvailable.length > 0;

  return (
    <>
      <DialogHeader>
        <DialogTitle className={'flex items-center'}>{t('Blockierte Positionen im Warenkorb')}</DialogTitle>
        <DialogDescription className={'block'}>
          {t('Folgende Positionen können nicht bestellt werden:')}
        </DialogDescription>
      </DialogHeader>
      <ScrollArea className="min-w-0 overflow-y-auto">
        {blockedPositionsOrderEndTimeExeeded.length > 0 && (
          <BlockedPositionsSection
            heading={t('Bestellschlusszeit abgelaufen')}
            positions={blockedPositionsOrderEndTimeExeeded}
          />
        )}
        {blockedPositionsNotAvailable.length > 0 && (
          <BlockedPositionsSection
            heading={t('Am gewählten Lieferdatum nicht verfügbar')}
            positions={blockedPositionsNotAvailable}
          />
        )}
        {blockedPositionsOtherReasons.length > 0 && (
          <BlockedPositionsSection
            heading={t('Andere blockierte Positionen')}
            positions={blockedPositionsOtherReasons}
          />
        )}
      </ScrollArea>
      {canChangeDeliveryDate && <p className="text-sm">{t('Bitte passen Sie das Lieferdatum an.')}</p>}
      <DialogFooter className={'flex-col desktop:flex-row gap-4 desktop:gap-1'}>
        {canChangeDeliveryDate ? (
          <>
            <Button
              variant="primaryOutline"
              onClick={acknowledgeBlockedPositionsDialog}
              data-testid={'cart-dialog-blocked-positions-cancel-button'}
            >
              {t('Abbrechen')}
            </Button>
            <Button
              onClick={changeDeliveryDateOfCart}
              data-testid={'cart-dialog-blocked-positions-change-date-button'}
            >
              {t('Lieferdatum ändern')}
            </Button>
          </>
        ) : (
          <Button
            onClick={acknowledgeBlockedPositionsDialog}
            data-testid={'cart-dialog-blocked-positions-acknowledge-button'}
          >
            {t('OK, ich verstehe')}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

function BlockedPositionsSection({ heading, positions }: Readonly<{ heading: string; positions: IOrderPositionDTOData[] }>) {
  const { t, i18n } = useTranslation();
  return (
    <div className="mb-4">
      <h4 className="font-semibold text-sm px-3 py-2">{heading}</h4>

      {/* Tabelle ab sm: – darunter (Mobile/Fullscreen) zu schmal, daher Karten-Layout */}
      <table className="hidden sm:table w-full table-fixed text-sm border-b">
        <colgroup>
          <col className="w-[10%]" />
          <col className="w-[15%]" />
          <col className="w-auto" />
          <col className="w-[20%]" />
          <col className="w-[12%]" />
        </colgroup>
        <thead className="bg-muted/40">
          <tr>
            <th className="text-left px-3 py-2">{t('Pos.')}</th>
            <th className="text-left px-3 py-2">{t('Art. Nr.')}</th>
            <th className="text-left px-3 py-2">{t('Artikelbezeichnung')}</th>
            <th className="text-left px-3 py-2">{t('Lieferdatum')}</th>
            <th className="text-right px-3 py-2">{t('Menge')}</th>
          </tr>
        </thead>
        <tbody>
          {positions.map(position => (
            <tr key={position.id} className="border-t">
              <td className="px-3 py-2 text-muted-foreground">{position.position}</td>
              <td className="px-3 py-2 text-muted-foreground">{position.articleBaseNo}</td>
              <td className="px-3 py-2">{position.articleDescriptionText}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {formatDateByLanguage(new Date(position.deliveryDate), i18n.language)}
              </td>
              <td className="px-3 py-2 text-right text-muted-foreground">{position.orderQuantity}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Karten-Layout für Mobile (< sm) – keine Tabelle, kein horizontaler Scrollbar */}
      <ul className="sm:hidden border-b text-sm">
        {positions.map(position => (
          <li key={position.id} className="border-t px-3 py-2">
            <p className="font-medium break-words">{position.articleDescriptionText}</p>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">{t('Pos.')}</dt>
                <dd>{position.position}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">{t('Art. Nr.')}</dt>
                <dd>{position.articleBaseNo}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">{t('Lieferdatum')}</dt>
                <dd>{formatDateByLanguage(new Date(position.deliveryDate), i18n.language)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">{t('Menge')}</dt>
                <dd>{position.orderQuantity}</dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}
